/**
 * @file Утилита для работы с Mirror API
 * @module membrane/src/services/mirrorApi
 *
 * Клиент для взаимодействия с Mirror API - единственной дверью данных для мембраны.
 * Все данные для Слоя 0 получаются через Mirror API.
 *
 * Режим VITE_MIRROR_STATIC=true: загрузка из статических JSON в /mirror/
 * (для деплоя без backend на Infomaniak Web Hosting).
 */

const isStatic = import.meta.env.VITE_MIRROR_STATIC === 'true';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const MIRROR_BASE = isStatic ? '' : API_BASE_URL;
const API_MIRROR = isStatic ? '/mirror' : `${MIRROR_BASE}/api/mirror`;

/**
 * Получает seed для вариативности
 */
export async function getSeed(): Promise<{ seed: string }> {
  if (isStatic) {
    return { seed: crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}` };
  }
  const response = await fetch(`${API_MIRROR}/seed`);
  if (!response.ok) {
    throw new Error('Failed to get seed');
  }
  return response.json();
}

/**
 * Получает параметры Слоя 0 (туманность)
 */
export async function getField(seed?: string): Promise<any> {
  const url = isStatic ? `${API_MIRROR}/field.json` : (seed ? `${API_MIRROR}/field?seed=${seed}` : `${API_MIRROR}/field`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to get field');
  }
  return response.json();
}

/**
 * Получает граф проекции (кристаллизация)
 */
export async function getMap(seed: string, focusX: number, focusY: number): Promise<{
  nodes: Array<{
    id: string;
    type: string;
    x: number;
    y: number;
    size: number;
    form: string;
    exposable: boolean;
  }>;
  edges: Array<{
    from: string;
    to: string;
    type: string;
    exposable: boolean;
  }>;
}> {
  const url = isStatic ? `${API_MIRROR}/map.json` : `${API_MIRROR}/map?seed=${seed}&focusX=${focusX}&focusY=${focusY}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to get map');
  }
  return response.json();
}

/**
 * Получает содержимое сферы
 */
export async function getSphere(id: string): Promise<any> {
  const safeId = encodeURIComponent(id);
  const url = isStatic ? `${API_MIRROR}/spheres/${safeId}.json` : `${API_MIRROR}/sphere/${safeId}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to get sphere');
  }
  return response.json();
}

/**
 * Получает поток связи
 */
export async function getLink(id: string): Promise<any> {
  if (isStatic) {
    let fileId = id;
    if (id.includes('-') && !id.startsWith('RR__')) {
      const res = await fetch(`${API_MIRROR}/links/by-edge.json`);
      if (res.ok) {
        const map = (await res.json()) as Record<string, string>;
        fileId = map[id] ?? id;
      }
    } else if (id.includes('::')) {
      fileId = id.replace(/::/g, '__');
    }
    const response = await fetch(`${API_MIRROR}/links/${encodeURIComponent(fileId)}.json`);
    if (!response.ok) {
      throw new Error('Failed to get link');
    }
    return response.json();
  }
  const response = await fetch(`${API_MIRROR}/link/${id}`);
  if (!response.ok) {
    throw new Error('Failed to get link');
  }
  return response.json();
}

/**
 * Сохраняет телеметрию (мягкие индикаторы внимания)
 */
export async function saveTelemetry(data: {
  depth?: number;
  timeSpent?: number;
  events?: any[];
  layer?: number;
}): Promise<{ success: boolean }> {
  if (isStatic) {
    return { success: true };
  }
  const response = await fetch(`${API_MIRROR}/telemetry`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to save telemetry');
  }
  return response.json();
}

/**
 * Отправляет отклик или форму «Нырнуть в глубину».
 * - С backend: POST /api/feedback (сохраняет в БД и отправляет на email)
 * - В static-режиме: POST /send.php (PHP на Infomaniak, без сторонних сервисов)
 */
export async function submitFeedback(data: {
  name?: string;
  contact?: string;
  message: string;
  depth?: number;
  sourceLayer?: number;
}): Promise<{ success: boolean; id?: number }> {
  const isStatic = import.meta.env.VITE_MIRROR_STATIC === 'true';

  if (isStatic) {
    const response = await fetch('/send.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name,
        contact: data.contact,
        message: data.message,
      }),
    });
    let result: { success: boolean; error?: string };
    try {
      result = await response.json();
    } catch {
      throw new Error(response.ok ? 'Invalid response' : `Ошибка ${response.status}. Проверьте настройки send.php на сервере.`);
    }
    if (!result.success) throw new Error(result.error || 'Failed to submit');
    return result;
  }

  const base = import.meta.env.VITE_API_URL || '';
  const url = base ? `${base.replace(/\/$/, '')}/api/feedback` : '/api/feedback';
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to submit feedback');
  return response.json();
}

/**
 * Получает структуру галереи (разделы и изображения)
 * При ошибке или пустой галерее возвращает { sections: [] }
 */
export async function getGallery(): Promise<{
  sections: Array<{
    name: string;
    cover: string;
    images: string[];
    count: number;
  }>;
}> {
  try {
    const url = isStatic ? `${API_MIRROR}/gallery.json` : `${API_MIRROR}/gallery`;
    const response = await fetch(url);
    if (!response.ok) {
      return { sections: [] };
    }
    const data = await response.json();
    return { sections: Array.isArray(data?.sections) ? data.sections : [] };
  } catch {
    return { sections: [] };
  }
}
